import IndicatorButton from 'components/indicator/IndicatorButton';
import { useAppState } from 'hooks/useAppState';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import { ChatState } from 'stores/atoms/chatState';

/* ----------------------------------------------------------------------- */
/* 型定義 */
/* ----------------------------------------------------------------------- */
export type MessageRole = 'user' | 'ai' | 'option';
export type MessagePayload =
  | { type: 'suggestion'; title: string; description: string }
  | { type: 'command'; prompt: string }
  | { type: 'coverImage' };
export type Message = {
  role: MessageRole;
  text: string;
  delay?: number;
  payload?: MessagePayload;
  disabled?: boolean;
};

export type WsChatSidebarConfig = {
  initialMessages?: string[];
  buttons?: string[];
  onMessage: (
    message: string,
    setMessages: (value: React.SetStateAction<Message[]>) => void,
    messages: Message[]
  ) => Promise<void>;
  onSuggestionSelect?: (
    payload: Extract<MessagePayload, { type: 'suggestion' }>,
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>,
    messages: Message[]
  ) => Promise<void>;
  onCoverImageGenerate?: (
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>
  ) => Promise<void>;
  errorMessage?: string;
};

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  config: WsChatSidebarConfig | null;
};

/**
 * チャットサイドバー
 */
export default function WsChatSidebar({ open, setOpen, messages, setMessages, config }: Props) {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const [acctState] = useAtom(AcctState);
  const [, setChatState] = useAtom(ChatState);
  const { getActivePlan, isAiTextCountNoLimit } = useAppState();

  // プラン情報
  const activePlan = getActivePlan();

  // ローディングの制御設定
  const [loading, setLoading] = useState(false);

  // チャット入力の制御設定
  const [input, setInput] = useState('');
  const [isComposing, setIsComposing] = useState(false); // IME変換中かどうかを追跡

  // 参照
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 最新の状態への参照を保持
  const openRef = useRef(open);
  const configRef = useRef(config);
  const messagesRef = useRef(messages);
  const setOpenRef = useRef(setOpen);
  // eslint-disable-next-line react-hooks/refs -- レンダーごとにrefを最新値に同期するパターン（イベントハンドラ用）
  openRef.current = open;
  // eslint-disable-next-line react-hooks/refs -- レンダーごとにrefを最新値に同期するパターン（イベントハンドラ用）
  configRef.current = config;
  // eslint-disable-next-line react-hooks/refs -- レンダーごとにrefを最新値に同期するパターン（イベントハンドラ用）
  messagesRef.current = messages;
  // eslint-disable-next-line react-hooks/refs -- レンダーごとにrefを最新値に同期するパターン（イベントハンドラ用）
  setOpenRef.current = setOpen;

  // チャット送信関数への参照
  const sendMessageRef = useRef<(message: string) => Promise<void>>(undefined);

  /* ----------------------------------------------------------------------- */
  /* 内部関数 */
  /* ----------------------------------------------------------------------- */

  /**
   * メッセージエリアの最下部にスクロール
   */
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  /**
   * テキストエリアの高さを自動調整
   */
  const adjustTextareaHeight = (textarea: HTMLTextAreaElement, value: string) => {
    textarea.style.height = 'auto';
    const lineCount = (value.match(/\n/g) || []).length + 1;
    const newHeight = Math.min(Math.max(50, 30 + lineCount * 20), 430);
    textarea.style.height = `${newHeight}px`;
  };

  /* ----------------------------------------------------------------------- */
  /* 副作用処理 */
  /* ----------------------------------------------------------------------- */

  // メッセージ追加時の自動スクロール
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // チャットサイドバーが開かれた時の初期化処理
  useEffect(() => {
    if (open && config && messages.length === 0) {
      // 初期メッセージを表示
      config.initialMessages?.forEach((message, idx) => {
        setTimeout(
          () => {
            const newMessage = {
              role: 'ai' as MessageRole,
              text: message,
            };
            setMessages(prev => [...prev, newMessage]);
          },
          500 + idx * 100
        );
      });

      // ボタンを表示
      config.buttons?.forEach((button, idx) => {
        setTimeout(
          () => {
            const newMessage = {
              role: 'option' as MessageRole,
              text: button,
            };
            setMessages(prev => [...prev, newMessage]);
          },
          500 + (config.initialMessages?.length || 0) * 100 + idx * 100
        );
      });
    }
  }, [open, config, messages.length, setMessages]);

  /**
   * 外部からメッセージをセットする関数
   * setOpen は親 prop のため毎レンダーで identity が変わる。ref 経由にすることで
   * useCallback の依存配列を空にして atom 更新による無限ループを防ぐ。
   */
  const setMessageFromExternal = useCallback(async (message: string) => {
    // チャットが閉じている場合は自動で開く
    if (!openRef.current) {
      setOpenRef.current(true);

      // チャットが開かれるまで少し待ってから処理を続行
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // 入力欄にメッセージをセット
    setInput(message);

    // textareaの高さを調整
    if (textareaRef.current) {
      adjustTextareaHeight(textareaRef.current, message);
    }
  }, []);

  // setMessage関数をチャット状態に設定
  useEffect(() => {
    setChatState(prev => ({
      ...prev,
      setMessage: setMessageFromExternal,
    }));
  }, [setChatState, setMessageFromExternal]);

  /**
   * チャット送信（AI応答をAPIで取得）
   */
  const sendMessage = useCallback(
    async (message: string) => {
      const currentConfig = configRef.current;
      const currentMessages = messagesRef.current;

      if (!currentConfig) return;

      // ユーザーメッセージを追加
      setMessages(prev => [...prev, { role: 'user' as MessageRole, text: message }]);
      setInput('');
      setLoading(true);

      try {
        const updatedMessages = [
          ...currentMessages,
          { role: 'user' as MessageRole, text: message },
        ];
        await currentConfig.onMessage(message, setMessages, updatedMessages);
      } catch (e) {
        console.debug('sendMessage error', e);
        await new Promise(resolve => setTimeout(resolve, 500));
        setMessages(prev => [
          ...prev,
          {
            role: 'ai' as MessageRole,
            text: 'AIによる生成に失敗しました。もう一度お試しください。',
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [setMessages]
  );

  // sendMessage関数への参照を更新
  // eslint-disable-next-line react-hooks/refs -- レンダーごとにrefを最新値に同期するパターン（イベントハンドラ用）
  sendMessageRef.current = sendMessage;

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラー */
  /* ----------------------------------------------------------------------- */

  /**
   * 送信ボタンクリック処理
   */
  const onSendClick = () => {
    if (!input.trim()) return;
    sendMessage(input);
  };

  /**
   * 選択肢クリック処理
   * - suggestion payload: メッセージ送信せず onSuggestionSelect のみ呼ぶ（概要取得中の多重クリックを防ぐため loading で囲む）。disabled は立てない（何度でも押せる既存挙動を保つ）
   * - command payload: 対象ボタンを disabled にしてから、確定文言をユーザー発言として送信する（sendMessage が全体生成 API を呼ぶ経路に乗る）
   * - coverImage payload: 対象ボタンを disabled にしてから onCoverImageGenerate を呼ぶ（config 未設定なら何もしない）
   * - payload なし: 従来どおり sendMessage にフォールバック
   */
  const onChoiceClick = async (msg: Message, index: number) => {
    if (_isChoiceBlocked(msg)) return;

    if (msg.payload?.type === 'suggestion') {
      setLoading(true);
      try {
        await configRef.current?.onSuggestionSelect?.(
          msg.payload,
          setMessages,
          messagesRef.current
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    if (msg.payload?.type === 'command') {
      setMessages(prev => prev.map((m, i) => (i === index ? { ...m, disabled: true } : m)));
      await sendMessage(msg.payload.prompt);
      return;
    }

    if (msg.payload?.type === 'coverImage') {
      if (!configRef.current?.onCoverImageGenerate) return;
      setMessages(prev => prev.map((m, i) => (i === index ? { ...m, disabled: true } : m)));
      setLoading(true);
      try {
        await configRef.current.onCoverImageGenerate(setMessages);
      } finally {
        setLoading(false);
      }
      return;
    }

    await sendMessage(msg.text);
  };

  /**
   * チャット閉じる処理
   */
  const onClose = () => {
    setOpen(false);
  };

  /* ----------------------------------------------------------------------- */
  /* チェック関数 */
  /* ----------------------------------------------------------------------- */

  /**
   * AIテキスト生成回数制限チェック
   */
  const _isAiTextCountLimit = () => {
    return acctState.company.aiTextUsageCount >= (activePlan?.aiTextCountLimit ?? 0);
  };

  /**
   * command ボタン専用のAIテキスト生成回数制限チェック。
   * `_isAiTextCountLimit` と異なり無制限プラン（`isAiTextCountNoLimit`）を見る。
   * 既存の送信ボタン・textarea の disabled 条件は無制限判定を見ない非対称が既にあるため、
   * 既存側は変更せず新規分のみ正しい側に寄せる（既存側の是正は TASK-839 で対応）。
   */
  const _isAiTextCountLimitForButton = (): boolean => {
    return (
      !isAiTextCountNoLimit() &&
      acctState.company.aiTextUsageCount >= (activePlan?.aiTextCountLimit ?? 0)
    );
  };

  /**
   * 選択肢が押せない状態かどうかの判定を1箇所に集約する。
   * クリック可能条件とクラス付与の両方がこれを参照することで、
   * 「押せないのに押せる見た目」になることを防ぐ。
   */
  const _isChoiceBlocked = (msg: Message): boolean =>
    msg.disabled === true || (msg.payload?.type === 'command' && _isAiTextCountLimitForButton());

  /* ----------------------------------------------------------------------- */
  /* レンダリング */
  /* ----------------------------------------------------------------------- */

  if (!open) return null;

  return (
    <>
      <div className="branq-chat-sidebar">
        <div className="branq-chat-header">
          <span>BranQ</span>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
        </div>
        <div className="branq-chat-body">
          <div className="branq-chat-messages">
            {messages.length === 0 && (
              <div className="text-muted">AIに質問や要望を入力してください。</div>
            )}
            {messages.map((msg, idx) => (
              <React.Fragment key={idx}>
                <>
                  {((idx === 0 && msg.role === 'ai') ||
                    (idx > 0 && messages[idx - 1].role !== 'ai' && msg.role === 'ai')) && (
                    <div className="message left" key={`ai-icon-${idx}`}>
                      <img className="branq-ai-icon" src="/img/icon/bot.png" alt="アイコン" />
                    </div>
                  )}
                  <div className={`message ${msg.role}`}>
                    <span
                      className={`message-content ${_isChoiceBlocked(msg) ? 'choseOption' : msg.role}`}
                      onClick={() => {
                        if (msg.role === 'option' && !loading && !_isChoiceBlocked(msg)) {
                          void onChoiceClick(msg, idx);
                        }
                      }}
                    >
                      {msg.text}
                    </span>
                  </div>
                </>
              </React.Fragment>
            ))}
            <div ref={messagesEndRef} />
            {/* AI考え中インジケーター */}
            {loading && (
              <div className="message left">
                <span className="message-content ai">
                  <span className="branq-ai-thinking">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </span>
                  AIが考え中...
                </span>
              </div>
            )}
          </div>
          <div className="branq-chat-editor position-relative">
            <textarea
              ref={textareaRef}
              className="form-control me-2"
              value={input}
              onChange={e => {
                setInput(e.target.value);
                adjustTextareaHeight(e.target, e.target.value);
              }}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={() => setIsComposing(false)}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.shiftKey && !isComposing) {
                  // ctrl+Enter / ⌘+Enter: 送信（IME変換中でない場合のみ）
                  e.preventDefault();
                  onSendClick();
                }
              }}
              placeholder="メッセージを入力..."
              disabled={loading}
            />
            <button
              className="branq-chat-send-button"
              onClick={() => {
                if (textareaRef.current) {
                  adjustTextareaHeight(textareaRef.current, '');
                }
                onSendClick();
              }}
              disabled={loading || !input.trim() || _isAiTextCountLimit()}
            >
              <IndicatorButton visible={loading} />
              <img className="branq-chat-send-icon" src="/img/icon/airplane.svg" alt="送信" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
